import { inspect } from 'util';
import * as R from 'ramda';
import * as Sentry from '@sentry/react';
import { CustomError } from '../errors';

// List of blacklist word that will be scrubbed from the logs
const piiPrivateBlacklist = [
  'secret',
  'secretKey',
  'token',
  'document',
  'acceptedTerms',
  'identityAuthToken',
  'acceptedDeclaration',
  'firstName',
  'lastName',
  'name',
  'email',
  'givenNames',
  'familyNames',
  'dateOfBirth',
  'gender',
  'evidences',
  'dateOfExpiry',
  'phoneNumber',
  'document.number',
  'X-Forwarded-For',
  'x-civic-feedback-token',
  'sourceIp',
  'ugimages.s3.amazonaws.com',
];

// Specific errors to ignore
export const ignoredErrors = [
  // Don't log when user rejects the connection request in their wallet
  /User rejected the request/,
  'No error message',
  'Uncompressed Asset',
  'No response from remote app for REQUEST_SIGNED_PROOF', // When user cancels the login
  // From facetec
  'play() can only be initiated by a user gesture',
  // Known error from HCaptcha - https://github.com/getsentry/sentry-javascript/issues/2514
  'Non-Error promise rejection captured with value: Timeout (h)',
];

// Specific URLs to ignore
export const denyUrls = [
  // Chrome extension errors should not be logged
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
];

export function containsBlacklistWords(input: string | undefined): boolean {
  if (R.isNil(input)) {
    return false;
  }
  return piiPrivateBlacklist.some((blacklistItem) => input?.toLowerCase()?.includes(blacklistItem));
}

export const beforeSend = (event: Sentry.Event): Sentry.Event => {
  const scrubbedEvent = event;

  // Scrub the relevant data fields
  if (scrubbedEvent.user) {
    // Don't send user's email address
    delete scrubbedEvent.user.email;
  }
  return scrubbedEvent;
};

export const beforeBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null => {
  const scrubbedCrumb = breadcrumb;
  // Check if the message contains any blacklisted words
  if (containsBlacklistWords(scrubbedCrumb.message)) {
    // Exclude the whole message if any blacklisted words are found
    scrubbedCrumb.message = '[Scrubbed blacklisted message]';
  }
  return scrubbedCrumb;
};

export const formatError = (error: Record<string, unknown>, excludeProps: Array<string>): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.getOwnPropertyNames(error).forEach((propName) => {
    if (!R.includes(propName, excludeProps)) {
      formatted[propName] = error[propName];
    }
  });
  return formatted;
};

export const getErrorContext = (error: Error | CustomError | null, level = 0): string[] => {
  if (error === null) {
    return [];
  }
  const errorList = [];
  // We can have nested error again and must go deeper if so
  if (R.is(CustomError, error)) {
    const customError = error as CustomError;
    const excludeProperties: Array<string> = ['nestedError'];
    const formattedError = formatError(customError as unknown as Record<string, unknown>, excludeProperties);
    const errorString = `${customError.constructor.name}\n${inspect(formattedError, {
      depth: null,
      compact: false,
    })}`;
    errorList.push(errorString);

    if (customError.nestedError) {
      const nestedErrorList = getErrorContext(customError.nestedError, level + 1);
      const newList = errorList.concat(nestedErrorList);
      return newList;
    }
    return errorList;
  }
  errorList.push(inspect(error, { depth: null, compact: false }) || 'No Error');
  return errorList;
};

export const setErrorContext = (message: string, error: Error): void => {
  // Only CustomError has nested errors
  if (R.is(CustomError, error)) {
    if ((error as CustomError).nestedError) {
      // Traverse down them and add to the list
      const nestedErrors = getErrorContext(error as CustomError);
      const context: Record<string, string> = {};
      nestedErrors.forEach((err, index) => {
        const propName = `${index + 1}`;
        context[propName] = err;
      });
      Sentry.setContext('Errors', context);
    }
  }
  Sentry.setContext('LogMessage', { message });
};
